import React, { useCallback } from "react";
import styles from "Components/NewOrder/index.module.scss";
import { useFormikContext } from "formik";
import { AppContext } from "Context/AppContext";
import { useEffect, useContext } from "react";
import { useOrderAttributeProvider } from "../context/order";
import { calcuateSummary, calculateKintsugiItemPrice, houseAccountTaxCalculation } from "Context/OrderContext/OrderCalculation";
import DiscountModal from "./OrderDiscount/OrderDiscount";
import TaxModal from "./OrderTax/OrderTax";
import { useState } from "react";
import FundModal from "./OrderFund";
import { getEstimateTax } from "api/order";
import { orderObject } from "../utility/createOrderObject";
import { useUser } from "Context/UserContext/useUser";
import moment from "moment";
import { checkPriceDefference } from "../utility/util";
import { ALLOW_KINTSUGI_TAX_DATE } from "Helpers/Constants";
import DeliveryAndService from "./DeliveryAndService";
import UpchargeModel from "Components/Modal/UpchargeUpdate/upcharge-update";
import Tax from "Components/Modal/Tax/tax";

const TotalSummary = (props) => {
  const formik = useFormikContext();
  const {
    summary,
    setSummary,
    module,
    orderDetail,
    modal,
    setModal,
    active,
  } = useOrderAttributeProvider();
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showServiceModel, setShowServiceModel] = useState(false);
  const [showUpcharge, setShowUpcharge] = useState(false);
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [showTax, setShowTax] = useState(false);
  const [tempAddFundValue, setTempAddFundValue] = useState(summary.add_fund);
  const [tempReFundValue, setTempReFundValue] = useState(summary.refund);
  const { setIsLoading, showToastMessage } = useContext(AppContext);
  const { userState, reLogin } = useUser();

  //get login user information
  const getUser = async () => {
    if (userState?.userProfile?.id) {
      return userState?.userProfile?.id;
    } else {
      const user = await reLogin();
      return user.id;
    }
  };

  //fetch taxable amount and tax related data from API
  //this function will be called when zipcode is entered and anything changes made in dependencies fields
  const taxCalculation = useCallback(async () => {
    try {

      // For house account payment type, we use a different tax calculation method
      if (formik.values.payment_type === "house_account" && formik.values.tax_amount > 0) {
        // Calculate the order summary (subtotal, discounts, etc.)
        const summary = calcuateSummary(formik);
        // Calculate tax specifically for house account orders using recipient data and summary
        const houseAccountData = houseAccountTaxCalculation(formik.values.recipients, summary);
        // Update the form values with the house account tax calculation results
        // This includes tax amount, tax rate, and other tax-related fields
        return formik.setValues((v) => ({...v, ...houseAccountData}));
      }
      
      const orderedDate = moment(orderDetail?.createdAt).format("YYYY-MM-DD HH:mm");
      const startDateKintsugiTax = ALLOW_KINTSUGI_TAX_DATE;
      //check if order is duplicate order or not
      const duplicateOrder =
        formik.values.order_type_identifier === 2 ? true : false;
      //check if order is pickup or delivery
      const pickupOrder =
        formik.values.delivery_information.order_type === 1 ? true : false;
      //check if zipcode is valid or not for delivery order to call getEstimateTax API
      const isValidZipcode =
        formik.values.delivery_information?.zipcode?.length === 5 &&
        formik.values.delivery_information?.zipcode !== "";
      // check if order is duplicate or not.
      // if duplicate order then call getEstimateTax API without checking the order date
      // if not duplicate order then check the order created date and call getEstimateTax API to avoid extra API call for edit order
      const isValidOrderDate =
        (!duplicateOrder && orderedDate > startDateKintsugiTax) ||
        duplicateOrder;

      //If the order is a duplicate, pickup or delivery and the zipcode is valid, then call the getEstimateTax API.
      //do not call the API if the order is a replacement order, the order date is less than the start date of the kintsugi tax, or the payment type is house account.
      //if the total quantity is 0 then do not call the API
      if (
        ((isValidZipcode && !pickupOrder) || pickupOrder) &&
        summary.total_qty > 0 &&
        isValidOrderDate &&
        !formik.values.is_replace_order &&
        formik.values.payment_type !== "house_account"
      ) {
        //prepare order object for tax calculation
        const summary = calcuateSummary(formik);
        const receipients = calculateKintsugiItemPrice(summary, formik);
        formik.setValues((v) => {
          return {
            ...v,
            recipients: receipients,
          };
        });
        const user_id = await getUser();
        const order = orderObject(formik.values, summary, user_id);
        const postBody = {
          order: order,
        };
        //call API to get tax calculated amount
        const estimatedTaxRes = await getEstimateTax(postBody);
        if (estimatedTaxRes && estimatedTaxRes?.status === 200) {
          await updatedEstmatedResToRecData(estimatedTaxRes.data.data.data);
        } else {
          showToastMessage(
            undefined,
            estimatedTaxRes.data.message || "Error in tax calculation",
            "error",
            false
          );
        }
      }
    } catch (error) {
      showToastMessage(
        undefined,
        error.message || "Error in tax calculation",
        "error",
        false
      );
    }
  }, [
    summary.sub_total,
    formik.values.delivery_information.delivery_type,
    formik.values.delivery_information.zipcode,
    formik.values.discount.discount_percentage,
    formik.values.discount.discount_price,
    summary.total_qty,
    formik.values.delivery_information.storeId,
    formik.values.delivery_information.charge,
    formik.values.tax_id,
    formik.values.payment_type,
    summary.discount_price,
    formik.values.tax_amount,
  ]);

  const updatedEstmatedResToRecData = async (data) => {
    const { order } = data;
    const {
      receipient,
      tax,
      is_tax_calculated_manually,
      tax_rate,
      taxable_amount,
    } = order[0];
    //update the tax amount and required related data to the recipient data
    const recipientsData = formik.values.recipients?.map((recipient, index) => {
      let recProductIndex = receipient.findIndex(
        (r) => r?.recipient_key === recipient?.recipient_key
      );
      receipient[recProductIndex]?.recipient_product?.map((product, index) => {
        if (product?.product_type == 0) {
          let productIndex = recipient.products.findIndex(
            (p) => p?.product_key === product?.product_key
          );
          if (productIndex !== -1) {
            recipient.products[productIndex].taxable_amount =
              +product?.taxable_amount || 0;
            recipient.products[productIndex].tax_rate = +product.tax_rate || 0;
            recipient.products[productIndex].tax = +product?.tax || 0;
            recipient.products[productIndex].kintsugi_product_id =
              product?.kintsugi_product_id || "";
            recipient.products[productIndex].kintsugi_item_price =
              +product?.kintsugi_item_price || 0;
            return recipient.products[productIndex];
          }
        } else if (product?.product_type == 1) {
          let productIndex = recipient.addons.findIndex(
            (p) => p?.product_key === product?.product_key
          );
          if (productIndex !== -1) {
            recipient.addons[productIndex].taxable_amount =
              +product?.taxable_amount || 0;
            recipient.addons[productIndex].tax_rate = +product?.tax_rate || 0;
            recipient.addons[productIndex].tax = +product?.tax || 0;
            recipient.addons[productIndex].kintsugi_product_id =
              product?.kintsugi_product_id || "";
            recipient.addons[productIndex].kintsugi_item_price =
              +product?.kintsugi_item_price || 0;
            return recipient.addons[productIndex];
          }
        }
      });
      return recipient;
    });

    //set the tax amount and updated recipients info to formik values
    formik.setValues((v) => {
      return {
        ...v,
        is_tax_calculated_manually: is_tax_calculated_manually,
        taxable_amount: +taxable_amount,
        recipients: recipientsData,
        tax_rate: +tax_rate,
        tax_amount: +tax,
      };
    });
  };

  //Reset the tax related data when payment_type = "house_account".
  const resetTaxCalRelatedData = useCallback(async () => {
    if ( summary.total_qty < 1  && formik.values.total_product_qty < 1 ) {
      formik.setValues((v) => {
        return {
          ...v,
          is_tax_calculated_manually: false,
          tax_rate: 0,
          tax_amount: 0,
          house_account_tax_amount: 0,
          taxable_amount: 0,
          recipients: v.recipients.map((recipient) => {
            return {
              ...recipient,
              products: recipient.products.map((product) => {
                return {
                  ...product,
                  tax: 0,
                  tax_rate: 0,
                  taxable_amount: 0,
                  kintsugi_item_price: 0,
                };
              }),
              addons: recipient.addons.map((addon) => {
                return {
                  ...addon,
                  tax: 0,
                  tax_rate: 0,
                  taxable_amount: 0,
                  kintsugi_item_price: 0,
                };
              }),
            };
          }),
        };
      });
    }
  }, [summary.total_qty < 1]);

  useEffect(() => {
    taxCalculation();
  }, [taxCalculation]);

  useEffect(() => {
    resetTaxCalRelatedData();
  }, [resetTaxCalRelatedData]);

  useEffect(() => {
    const summ = calcuateSummary(formik);
    setSummary(summ);
    //Update formik total_qty value
    formik.setValues((v) => {
      return {
        ...v,
        total_product_qty: summ.total_qty,
      };
    });
  }, [
    formik.values.recipients,
    formik.values.delivery_information.charge,
    formik.values.delivery_information.upcharge_amount,
    formik.values.discount,
    formik.values.tax_id,
    formik.values.payment_type,
    formik.values.taxable_amount,
    formik.values.tax_amount,
  ]);

  useEffect(() => {
    setTempAddFundValue(summary.add_fund);
    setTempReFundValue(summary.refund);
  }, [summary.add_fund, summary.refund]);

  let delivery_charge =
    summary.delivery_charges === 0
      ? 0
      : +summary.delivery_charges;

  const changeValue = (value) => {
    let val = value || "";
    setTempAddFundValue(val);
  };
  const changeValueRefund = (value) => {
    let val = value || "";
    setTempReFundValue(val);
  };

  //check if the tax amount and delivery charge amount is same as the order detail tax and delivery charge amount
  const isDeliveryChargeDeff = checkPriceDefference(delivery_charge, orderDetail?.delivery_charges);
  const isTaxDeff = checkPriceDefference(summary.tax, orderDetail?.tax);
  const isUpchargeDeff = checkPriceDefference(summary.upcharge_amount, orderDetail?.upcharge_amount);

  const paymentType = formik.values.payment_type;
  //Identify if the order is a replacement order or not
  const replacementOrder = formik.values.order_type_identifier === 1;

  return (
    <div>
      <div className={`${styles.subTotalBlock}`}>
        {/* subtotal */}
        <div className={`${styles.totalBlockItem} mb-2`}>
          <span>Sub Total</span>
          <span>${summary.sub_total.toFixed(2)}</span>
        </div>
        {/* discount */}
        { !replacementOrder && (
          <div
            className={`${styles.totalBlockItem} align-items-center mb-2`}
            style={{ position: "relative" }}
          >
            <span>
              Discount
              <span
                className={`btn-sm outline-btn ml-3 cursor-pointer ${styles.totalBlockBtn}`}
                onClick={() => setShowDiscountModal(active === 0 ? true : false)}
              >
                + Discount
              </span>
              <br></br>
              {formik.values?.discount?.discount_promocode && (
                <span
                  style={{
                    fontSize: "13px",
                    position: "absolute",
                    bottom: "-10px",
                    margin: 0,
                    padding: 0,
                    lineHeight: 1,
                  }}
                >
                  {"(Promo Code : " +
                    formik.values?.discount?.discount_promocode +
                    ")"}
                </span>
              )}
            </span>
            <span className={`${styles.borderBottom}`}>
              - ${parseFloat(summary.discount_price).toFixed(2)}
            </span>
          </div>  
        )}
        {/* Replacement Discount */}
        {replacementOrder && (
          <div
            className={`${styles.totalBlockItem} align-items-center mb-2`}
            style={{ position: "relative" }}
          >
            <span>
              Replacement Discount
              <br></br>
            </span>
            <span className={`${styles.borderBottom}`}>
                - ${parseFloat(summary.replacement_discount_amount).toFixed(2)}
            </span>
          </div>
        )}
        {/* Subscription discount */}
        {orderDetail?.is_subscribe_order && (
          <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
            <span>Subscription Discount</span>
            <span className={`${styles.borderBottom}`}>
              - ${summary.subscription_discount}
            </span>
          </div>
        )}

        {/* // redeem amount */}
        {module === "edit-order" && (
          <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
            <span>User Redeem Amount</span>
            <span>
              $
              {formik.values.petal_reward.petal_rewards_point_amount.toFixed(2)}
            </span>
          </div>
        )}

        {/* tax */}
        <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
          <span>
            Tax
            {paymentType === "house_account" ? (
              <span
                className={`btn-sm outline-btn ml-3 cursor-pointer ${styles.totalBlockBtn}`}
                onClick={() => setShowTax(active === 0 ? true : false)}
              >
                + Add Tax
              </span>
            ):  
              <span
                  className={`btn-sm outline-btn ml-3 cursor-pointer ${styles.totalBlockBtn}`}
                  onClick={() => setShowTaxModal(active === 0 ? true : false)}
                >
                + Tax ID
              </span>
            }
          </span>
          <span>
            {module === "edit-order" && !isTaxDeff &&(
              <small style={{ color: "#F37C7C", fontSize:14 }}> 
                {`(${parseFloat(orderDetail?.tax || 0).toFixed(2)}) `}
              </small>
            )} 
            ${summary?.tax?.toFixed(2)}
          </span>
        </div>
        {/* delivery charge */}
        <div className={`${styles.totalBlockItem} mb-2`}>
          <span>
            Service/Delivery
            {
              props.module === 'edit-order' && formik.values.delivery_information.order_type === 0 ? <span
                className={`btn-sm outline-btn ml-3 cursor-pointer ${styles.totalBlockBtn}`}
                onClick={() => setShowServiceModel(true)}
              >
                Service/Delivery
              </span> : null
            }

          </span>
          <span>
            {module === "edit-order" && !isDeliveryChargeDeff && (
              <small style={{ color: "#F37C7C", fontSize:14 }}> 
                {`(${parseFloat(orderDetail?.delivery_charges || 0).toFixed(2)}) `}
              </small>
            )}
            ${parseFloat(delivery_charge).toFixed(2)}
          </span>
        </div>
          {/* Upcharge */}
        <div className={`${styles.totalBlockItem} mb-2`}>
          <span>
          Upcharge
            {
              props.module === 'edit-order' && formik.values.delivery_information.order_type === 0 ? <span
                className={`btn-sm outline-btn ml-3 cursor-pointer ${styles.totalBlockBtn}`}
                onClick={() => setShowUpcharge(true)}
              >
                Upcharge
              </span> : null
            }

          </span>
          <span>
            {module === "edit-order" && !isUpchargeDeff && (
              <small style={{ color: "#F37C7C", fontSize:14 }}> 
                {`(${parseFloat(orderDetail.upcharge_amount || 0).toFixed(2)}) `}
              </small>
            )}
            ${parseFloat(summary.upcharge_amount).toFixed(2)}
          </span>
        </div>

        {/* add fund and refund */}
        {module === "edit-order" && (
          <>
            <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
              <span>
                Add Funds
                {/* <span
                  className={`btn-sm outline-btn ml-3 cursor-pointer ${styles.totalBlockBtn}`}
                  onClick={() => {
                    if (+parseFloat(summary.refund).toFixed(0) > 0) {
                      showToastMessage(
                        undefined,
                        `Can not add fund while there is a refund amount available.`,
                        "error",
                        false
                      );
                      return;
                    }
                    if (active === 0) setModal("fund-edit");
                  }}
                >
                  + Add Funds
                </span> */}
              </span>
              <span>${parseFloat(summary.add_fund).toFixed(2)}</span>
            </div>
            <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
              <span>
                Refunds
                {/* <span
                  className={`btn-sm outline-btn ml-3 cursor-pointer ${styles.totalBlockBtn}`}
                  onClick={() => {
                    if (+parseFloat(summary.add_fund).toFixed(0) > 0) {
                      showToastMessage(
                        undefined,
                        `Can not refund while there is a add fund amount available.`,
                        "error",
                        false
                      );
                      return;
                    }
                    if (active === 0) setModal("refund-edit");
                  }}
                >
                  + Refund
                </span> */}
              </span>
              <span>-${parseFloat(summary.refund).toFixed(2)}</span>
            </div>
          </>
        )}
      </div>
      {/* total */}
      <div className={`${styles.totalBlock} mt-2`}>
        <div className={`${styles.mainTotalBlockItem} mb-2`}>
          <span>Total</span>
          <span>${summary.total.toFixed(2)}</span>
        </div>
        <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
          <span style={{ fontSize: "14px" }}>
            (This amount is the total cart amount. Additional Add fund is not
            considered in this total).
          </span>
        </div>
        {/* General info of amount */}
        {module === "edit-order" && (
          <>
            <div
              className={`${styles.totalBlockItem} align-items-center mb-2`}
              style={{ fontSize: "14px" }}
            >
              <span>Amount recieved till now</span>
              <span>${formik.values.total_fund.toFixed(2)}</span>
            </div>
            <div
              className={`${styles.totalBlockItem} align-items-center mb-2`}
              style={{ fontSize: "14px" }}
            >
              <span>Amount refunded till now</span>
              <span>-${formik.values.total_refund.toFixed(2)}</span>
            </div>
          </>
        )}
        {/* discount reason */}
        {formik.values.discount.discount_reason && (
          <div>
            <div className={`${styles.totalBlockItem} mb-2`}>
              <span>Discount Reason</span>
            </div>
            <div className={`${styles.totalBlockItem} mb-2`}>
              <span>{formik.values.discount.discount_reason}</span>
            </div>
          </div>
        )}
      </div>

      <DeliveryAndService
        showModal={showServiceModel}
        pageTitle="Service/Delivery"
        serviceChargeData={delivery_charge ? parseFloat(delivery_charge).toFixed(2) : 0}
        handleCancelClick={(e) => {
          setShowServiceModel(false);
        }}
        handleSuccess={(charge) => {
          setShowServiceModel(false);
        }}
      />

      <UpchargeModel
        showModal={showUpcharge}
        pageTitle="Upcharge"
        upchargeValue={summary.upcharge_amount ? parseFloat(summary.upcharge_amount).toFixed(2) : 0}
        handleCancelClick={(e) => {
          setShowUpcharge(false);
        }}
        handleClick={() => {
          setShowUpcharge(false);
        }}
        id={"upcharge"}
      />

      <DiscountModal
        showModal={showDiscountModal}
        pageTitle="Add Discount"
        submitStatus={false}
        handleCancelClick={(e) => {
          setShowDiscountModal(false);
        }}
      />
      <TaxModal
        showModal={showTaxModal}
        pageTitle="Add Tax ID"
        handleCancelClick={(e) => {
          setShowTaxModal(false);
        }}
      />
      <Tax
        showModal={showTax}
        pageTitle="Add Tax"
        submitStatus={false}
        handleCancelClick={(e) => {
          setShowTax(false);
        }}
      />
      <FundModal
        open={modal === "fund-edit"}
        pageTitle={"Add Fund"}
        disebled={true}
        value={tempAddFundValue}
        onClose={() => {
          setTempAddFundValue(summary.add_fund);
          setModal("");
        }}
        onChange={changeValue}
        onSave={() => {
          setSummary({
            ...summary,
            add_fund: tempAddFundValue,
          });
          setModal("");
        }}
      />
      <FundModal
        open={modal === "refund-edit"}
        pageTitle={"Refund"}
        disebled={true}
        value={tempReFundValue}
        onClose={() => {
          setTempReFundValue(summary.refund);
          setModal("");
        }}
        onChange={changeValueRefund}
        onSave={() => {
          setSummary({
            ...summary,
            refund: tempReFundValue,
          });
          setModal("");
        }}
      />
    </div>
  );
};
export default TotalSummary;
